import {Collapse, Divider} from "@mui/material";
import React, {useState} from "react";
import {formatCurrency} from "../../../../helpers";
import {IconChevronUp} from "../../../../ui/assets";
import {Card, Colors, Container, ITextProps, Text} from "../../../../ui";
import {FormattedDate} from "../../../components";
import {getEnumLabelKey, paymentPlanFrequencyInfoMap} from "../../../constants";
import {useIsMobile} from "../../../helpers";
import {AccountExternal, BundleAccountBreakdown, UserDashboardBase} from "../../../declarations";
import {getDisplayFourDigits} from "../utils";
import {BundleBreakdownRow} from "./BundleBreakdownRow";
interface IFieldValue {
  field: string;
  value: number;
  weight?: ITextProps["fontWeight"];
}

const FieldValue = ({field, value, weight = "regular"}: IFieldValue) => {
  return (
    <>
      <Divider sx={{width: "100%"}} />
      <Container fullWidth row sx={{justifyContent: "space-between"}}>
        <Text fontWeight={weight}>{field}:</Text>
        <Text fontWeight={weight}>{formatCurrency(value)}</Text>
      </Container>
    </>
  );
};

interface IBundleBalanceBreakdown {
  breakdown: BundleAccountBreakdown[];
  totalPaid: number;
  totalAmount: number;
  onItemClick?: (accountId: number) => void;
  userDashboard?: UserDashboardBase;
  defaultOpen?: boolean;
  showSubProduct?: boolean;
}

export const BundleBalanceBreakdown = ({
  breakdown,
  totalPaid,
  totalAmount,
  onItemClick,
  userDashboard,
  defaultOpen = false,
  showSubProduct,
}: IBundleBalanceBreakdown) => {
  const isMobile = useIsMobile();
  const [breakdownOpen, setBreakdownOpen] = useState(defaultOpen);
  const displayFourDigits = getDisplayFourDigits(userDashboard);
  const paymentMethodValue = displayFourDigits
    ? `${userDashboard!.last_4_digits} - ${userDashboard!.payment_method_type?.toUpperCase()}`
    : "Non-AutoPay";
  const endDateValue = userDashboard?.last_payment_timestamp ? (
    <FormattedDate date={userDashboard.last_payment_timestamp * 1000} />
  ) : (
    "N/A"
  );

  const planFields = userDashboard
    ? [
        {
          label: "Payment frequency",
          value: getEnumLabelKey(paymentPlanFrequencyInfoMap, userDashboard.installment_frequency),
        },
        {label: "Payment method", value: paymentMethodValue},
        {label: "End date", value: endDateValue},
      ]
    : [];

  return (
    <Card fullWidth sx={{alignItems: "start", gap: "16px", padding: "20px"}}>
      <Text size={isMobile ? 12 : 14} variant="inter600" color={Colors.grey._700} sx={{textTransform: "uppercase"}}>
        Plan Properties
      </Text>
      {planFields.length > 0 && (
        <>
          <Container fullWidth row={!isMobile} sx={{gap: "4px"}}>
            {planFields.map(({label, value}) => (
              <Container key={label} fullWidth sx={{alignItems: "start", gap: "4px"}}>
                <Text size={12} variant="inter400" color={Colors.grey._700}>
                  {label}
                </Text>
                <Text size={16} variant="inter400">
                  {value}
                </Text>
              </Container>
            ))}
          </Container>
          <Divider sx={{width: "100%"}} />
        </>
      )}
      <Container
        fullWidth
        row
        sx={{justifyContent: "space-between", alignItems: "center", cursor: "pointer"}}
        onClick={() => setBreakdownOpen((v) => !v)}
      >
        <Text size={isMobile ? 12 : 14} variant="inter600" color={Colors.grey._700} sx={{textTransform: "uppercase"}}>
          Balance Breakdown
        </Text>
        <IconChevronUp color={Colors.grey._700} style={breakdownOpen ? undefined : {transform: "rotate(180deg)"}} />
      </Container>
      <Collapse in={breakdownOpen} timeout={300} unmountOnExit sx={{width: "100%"}}>
        <Container fullWidth sx={{gap: "16px"}}>
          {breakdown.map((item) => (
            <BundleBreakdownRow
              key={item.account_public_id}
              item={item}
              onItemClick={onItemClick}
              showSubProduct={showSubProduct}
            />
          ))}
        </Container>
      </Collapse>
      <Divider sx={{width: "100%"}} />
      <Container
        fullWidth
        row={!isMobile}
        sx={{
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? "4px" : "0px",
        }}
      >
        <Text size={16} variant="inter600" color={Colors.grey._800}>
          Total bundle balance
        </Text>
        <Container row sx={{gap: "4px", flexShrink: 0}}>
          <Text size={16} variant="interDisplay400" sx={{whiteSpace: "nowrap"}} color={Colors.green._900}>
            {`Paid ${formatCurrency(totalPaid)}`}
          </Text>
          <Text size={16} variant="interDisplay400" sx={{whiteSpace: "nowrap"}} color={Colors.grey._800}>
            {`/ from ${formatCurrency(totalAmount)}`}
          </Text>
        </Container>
      </Container>
    </Card>
  );
};

export const BalanceBreakdown = ({account}: {account?: AccountExternal}) => {
  if (!account) {
    return null;
  }

  const {
    itemization_date,
    total_balance_on_itemization_date,
    total_interest_accrued_since_itemization_date,
    total_fees_accrued_since_itemization_date,
    total_paid_on_account_since_itemization_date,
    total_balance,
    collects_delinquent_amounts,
  } = account;

  return (
    <Container sx={{width: "90%", gap: "5px", paddingBottom: "20px"}}>
      <Text>
        As of <FormattedDate date={itemization_date} />, you owed: {formatCurrency(total_balance_on_itemization_date)}.
        Since then you were charged:{" "}
      </Text>
      <FieldValue field={"Interest"} value={total_interest_accrued_since_itemization_date} />
      <FieldValue field={"Fees"} value={total_fees_accrued_since_itemization_date} />
      <FieldValue
        field={"You paid or were credited this amount"}
        value={total_paid_on_account_since_itemization_date}
      />
      <FieldValue
        field={collects_delinquent_amounts ? "Delinquent balance" : "Total balance due"}
        value={total_balance}
        weight={"medium"}
      />
    </Container>
  );
};
